// SPDX-License-Identifier: GPL-3.0-or-later
// Canvas types and the brand palette for the table art (B3).
// The art code draws on a `CanvasLike`. It does not import DOM types or three, so the
// server config and the tests can use it. A test passes a fake canvas that records calls.

/** The part of CanvasRenderingContext2D that the art code uses. */
export interface Ctx2DLike {
  fillStyle: unknown;
  strokeStyle: unknown;
  lineWidth: number;
  font: string;
  textAlign: string;
  textBaseline: string;
  globalAlpha: number;
  fillRect(x: number, y: number, w: number, h: number): void;
  strokeRect(x: number, y: number, w: number, h: number): void;
  fillText(text: string, x: number, y: number, maxWidth?: number): void;
  strokeText(text: string, x: number, y: number, maxWidth?: number): void;
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  closePath(): void;
  arc(x: number, y: number, r: number, start: number, end: number): void;
  stroke(): void;
  fill(): void;
  save(): void;
  restore(): void;
  translate(x: number, y: number): void;
  rotate(angle: number): void;
  scale(x: number, y: number): void;
}

/** A canvas. An HTMLCanvasElement fits this shape. */
export interface CanvasLike {
  width: number;
  height: number;
  getContext(contextId: "2d"): Ctx2DLike | null;
}

/** Make a canvas of a size. The client gives a DOM canvas. A test gives a fake. */
export type CreateCanvas = (width: number, height: number) => CanvasLike;

/** A canvas from the page. Throw when there is no `document` (a server or a test without a canvas). */
export const defaultCreateCanvas: CreateCanvas = (width, height) => {
  const doc = (globalThis as { document?: { createElement(tag: "canvas"): CanvasLike } }).document;
  if (!doc) throw new Error("no document: pass createCanvas to the character registry");
  const canvas = doc.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

/** Get the 2D context. Throw when the canvas has none. */
export function context2d(canvas: CanvasLike): Ctx2DLike {
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2d canvas context is not available");
  return ctx;
}

/** Brand colors (THEME.md): field, mark (fills and focus rings only), edge, and neutral grays. */
export const BRAND = {
  field: "#000000",
  mark: "#FF0000",
  edge: "#FFFFFF",
  gray222: "#222222",
  gray333: "#333333",
  gray444: "#444444",
  gray666: "#666666",
  gray888: "#888888",
  grayCCC: "#CCCCCC",
} as const;

/** The same colors as numbers, for three.js materials. */
export const BRAND_HEX = {
  field: 0x000000,
  mark: 0xff0000,
  edge: 0xffffff,
  gray222: 0x222222,
  gray333: 0x333333,
  gray444: 0x444444,
  gray666: 0x666666,
  gray888: 0x888888,
  grayCCC: 0xcccccc,
} as const;

/** Monospace bold font string of a size in pixels. */
export function monoFont(px: number): string {
  return `bold ${Math.max(6, Math.round(px))}px monospace`;
}
